import React, { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { schrankApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';

export function SchrankFloorPreview({ floor, onOpen }) {
  const [preview, setPreview] = useState({ id: null, rooms: [], loading: true, failed: false });
  useEffect(() => {
    let active = true;
    if (!floor) return;
    setPreview({ id: floor.id, rooms: [], loading: true, failed: false });
    schrankApi.listRooms(floor.id)
      .then(({ data }) => { if (active) setPreview({ id: floor.id, rooms: data, loading: false, failed: false }); })
      .catch(() => { if (active) setPreview({ id: floor.id, rooms: [], loading: false, failed: true }); });
    return () => { active = false; };
  }, [floor]);
  if (!floor) return <div className="schrank-empty">Wähle eine Etage, um den Grundriss zu bearbeiten.</div>;
  const loading = preview.loading || preview.id !== floor.id;
  const width = floor.geometry?.width || 1200;
  const height = floor.geometry?.height || 800;
  // Frame the actual rooms, not the unused extent of the floor canvas.
  const bounds = preview.rooms.reduce((box, room) => {
    const g = room.geometry || {};
    const x = g.x || 0, y = g.y || 0;
    return { left: Math.min(box.left, x), top: Math.min(box.top, y),
      right: Math.max(box.right, x + (g.width || 1)), bottom: Math.max(box.bottom, y + (g.height || 1)) };
  }, { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity });
  const contentWidth = preview.rooms.length ? bounds.right - bounds.left : width;
  const contentHeight = preview.rooms.length ? bounds.bottom - bounds.top : height;
  const padding = Math.max(contentWidth, contentHeight) * 0.02;
  const viewWidth = contentWidth + padding * 2;
  const viewHeight = contentHeight + padding * 2;
  const originX = preview.rooms.length ? bounds.left - padding : 0;
  const originY = preview.rooms.length ? bounds.top - padding : 0;

  return <section className="schrank-preview" data-testid="floor-preview">
    <div className="schrank-preview-head"><div><h2>{floor.name}</h2>
      <p>{loading ? 'Grundriss wird geladen …' : preview.failed ? 'Vorschau nicht verfügbar' : `${preview.rooms.length} ${preview.rooms.length === 1 ? 'Raum' : 'Räume'}`}</p></div>
      <Button onClick={onOpen}><Pencil size={16} className="mr-2" /> Grundriss bearbeiten</Button>
    </div>
    <div className="schrank-preview-canvas">
      {loading ? <Skeleton className="h-72" /> : preview.failed ? <p role="alert" className="schrank-empty">Die Raumvorschau konnte nicht geladen werden.</p> : !preview.rooms.length ?
        <div className="schrank-empty">Noch keine Räume angelegt. Öffne die Grundrissbearbeitung, um Räume einzuzeichnen.</div> :
        <svg viewBox={`${originX} ${originY} ${viewWidth} ${viewHeight}`} width={viewWidth} height={viewHeight} role="img" aria-label={`Grundriss ${floor.name}`}>
          <rect width={width} height={height} fill="hsl(var(--background))" />
          {preview.rooms.map(room => {
            const g = room.geometry || {};
            return <g key={room.id}><title>{room.name}</title>
              <rect x={g.x || 0} y={g.y || 0} width={g.width || 1} height={g.height || 1} fill={g.color || 'hsl(var(--muted))'} stroke="hsl(var(--muted-foreground))" strokeWidth="1.5" />
              <text x={(g.x || 0) + (g.width || 0) / 2} y={(g.y || 0) + (g.height || 0) / 2} textAnchor="middle" dominantBaseline="middle" fontSize={Math.max(5, Math.min(viewWidth * 0.018, (g.width || 0) / Math.max(room.name.length, 1) * 1.5))} fill="#1e293b">{room.name}</text>
            </g>;
          })}
        </svg>}
    </div>
    <p className="schrank-preview-footer">Grundriss bearbeiten → Raum auswählen → Schränke bearbeiten</p>
  </section>;
}
